import React, { useState } from "react";
import { Link } from "react-router-dom";
import { User2, Eye, EyeOff, User } from "lucide-react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';

import { auth } from "../../config/firebase/firebaseConfig";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";

const PasswordSettings = () => {
  document.title = "Password Setting | LyfeAdmin";

  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    photoURL: ''
  });

  React.useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUserInfo({
        name: currentUser.displayName || 'Admin',
        email: currentUser.email || '',
        photoURL: currentUser.photoURL || ''
      });
    }
  }, []);

  const validation = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Please Enter Your Current Password"),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please Enter Your New Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required("Please Confirm Your New Password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          const credential = EmailAuthProvider.credential(
            currentUser.email,
            values.currentPassword
          );
          
          await reauthenticateWithCredential(currentUser, credential);

          await updatePassword(currentUser, values.newPassword);
          
          toast.success("Password updated successfully!");
          resetForm();
        }
      } catch (error: any) {
        console.error("Password update error:", error);
        let errorMessage = "Failed to update password";
        
        if (error.code === 'auth/wrong-password') {
          errorMessage = "Current password is incorrect";
        } else if (error.code === 'auth/weak-password') {
          errorMessage = "New password is too weak";
        } else if (error.code === 'auth/requires-recent-login') {
          errorMessage = "Please log out and log back in before changing your password";
        }
        
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  const getProfileImage = () => {
    if (userInfo.photoURL) return userInfo.photoURL;
    return null; // Return null to show icon instead
  };

  const hasProfileImage = () => {
    return userInfo.photoURL;
  };

  return (
    <React.Fragment>
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex items-center justify-between mb-6 mt-8">
          <h4 className="text-lg font-medium text-slate-700 dark:text-zink-100">Password Setting</h4>
          <div className="flex items-center gap-2">
            <Link
              to="/profile"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-custom-500 bg-white border border-custom-500 rounded-md hover:bg-custom-50 hover:border-custom-600 focus:bg-custom-50 focus:border-custom-600 focus:ring focus:ring-custom-100 active:bg-custom-50 active:border-custom-600 active:ring active:ring-custom-100 dark:bg-zink-700 dark:border-custom-500 dark:text-custom-500 dark:hover:bg-custom-500/10 dark:focus:bg-custom-500/10 dark:active:bg-custom-500/10"
            >
              <User2 className="inline-block size-4 mr-1" />
              Profile Setting
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-5 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="card">
              <div className="card-body p-0">
                <div className="flex items-center p-4 bg-custom-500 text-white">
                  <div className="shrink-0">
                    {hasProfileImage() ? (
                      <img
                        src={getProfileImage()}
                        alt="Profile"
                        className="size-16 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="size-16 rounded-full border-2 border-white bg-white/20 flex items-center justify-center">
                        <User className="size-8 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <h4 className="text-white font-medium">{userInfo.name || 'Admin'}</h4>
                  </div>
                </div>
                <div className="p-0">
                  <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-zink-500">
                    <span className="text-slate-500 dark:text-zink-300">Name</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{userInfo.name || 'Admin'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-zink-500">
                    <span className="text-slate-500 dark:text-zink-300">Username</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">admin</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <span className="text-slate-500 dark:text-zink-300">Email</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{userInfo.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-9">
            <div className="card">
              <div className="card-body">
                <h5 className="mb-4 text-16 font-medium border-b border-slate-200 dark:border-zink-500 pb-2">
                  Change Password
                </h5>

                <form onSubmit={validation.handleSubmit}>
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <label htmlFor="currentPassword" className="inline-block mb-2 text-base font-medium">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          id="currentPassword"
                          name="currentPassword"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pr-12"
                          placeholder="Enter current password"
                          value={validation.values.currentPassword}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="size-4 text-slate-400" />
                          ) : (
                            <Eye className="size-4 text-slate-400" />
                          )}
                        </button>
                      </div>
                      {validation.touched.currentPassword && validation.errors.currentPassword && (
                        <div className="mt-1 text-sm text-red-500">{validation.errors.currentPassword}</div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="inline-block mb-2 text-base font-medium">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          id="newPassword"
                          name="newPassword"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pr-12"
                          placeholder="Enter new password"
                          value={validation.values.newPassword}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="size-4 text-slate-400" />
                          ) : (
                            <Eye className="size-4 text-slate-400" />
                          )}
                        </button>
                      </div>
                      {validation.touched.newPassword && validation.errors.newPassword && (
                        <div className="mt-1 text-sm text-red-500">{validation.errors.newPassword}</div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="inline-block mb-2 text-base font-medium">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          name="confirmPassword"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pr-12"
                          placeholder="Confirm new password"
                          value={validation.values.confirmPassword}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="size-4 text-slate-400" />
                          ) : (
                            <Eye className="size-4 text-slate-400" />
                          )}
                        </button>
                      </div>
                      {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                        <div className="mt-1 text-sm text-red-500">{validation.errors.confirmPassword}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed w-full lg:w-auto"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-2-line animate-spin mr-2"></i>
                          Updating...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default PasswordSettings;